import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { MessageCircle, Check, ExternalLink, RefreshCw, Bell } from "lucide-react";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const WhatsAppNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem("enterprise_token");
      const response = await axios.get(`${API}/enterprise/whatsapp-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Erè nan chaje notifikasyon yo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsSent = async (notificationId) => {
    try {
      const token = localStorage.getItem("enterprise_token");
      await axios.post(
        `${API}/enterprise/whatsapp-notifications/${notificationId}/mark-sent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Notifikasyon make kòm voye!");
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification:", error);
      toast.error("Erè nan make notifikasyon");
    }
  };

  const openWhatsApp = (link, notificationId) => {
    window.open(link, "_blank");
    // Automatically mark as sent after opening
    setTimeout(() => markAsSent(notificationId), 2000);
  };

  const getNotificationTypeBadge = (type) => {
    const types = {
      new_order: { label: "Nouvo Kòmand", color: "bg-green-500" },
      pending_approval: { label: "Ap Tann Apwobasyon", color: "bg-yellow-500" },
      order_approved: { label: "Apwouve", color: "bg-blue-500" },
      order_rejected: { label: "Rejte", color: "bg-red-500" }
    };
    const config = types[type] || { label: type, color: "bg-gray-500" };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  return (
    <EnterpriseLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-500/20 rounded-xl">
              <MessageCircle className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Notifikasyon WhatsApp</h1>
              <p className="text-gray-500">Jere notifikasyon WhatsApp pou kòmand yo</p>
            </div>
          </div>
          <Button
            onClick={fetchNotifications}
            variant="outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Rafrechi
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Bell className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{notifications.length}</p>
                  <p className="text-sm text-gray-500">Notifikasyon an atant</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <MessageCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">+1 203 864 7557</p>
                  <p className="text-sm text-gray-500">Nimewo Admin</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Ap chaje...</p>
          </div>
        ) : notifications.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Pa gen notifikasyon</h3>
              <p className="text-gray-500">
                Tout notifikasyon WhatsApp yo voye deja!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <Card key={notification.id}>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        {getNotificationTypeBadge(notification.notification_type)}
                        <span className="text-xs text-gray-400">
                          {new Date(notification.created_at).toLocaleString('ht-HT')}
                        </span>
                      </div>
                      <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans bg-gray-50 p-4 rounded-lg mt-3">
                        {notification.message}
                      </pre>
                      <p className="text-xs text-gray-400 mt-2">
                        Pou: {notification.phone}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        onClick={() => openWhatsApp(notification.whatsapp_link, notification.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Ouvri WhatsApp
                      </Button>
                      <Button
                        onClick={() => markAsSent(notification.id)}
                        variant="outline"
                      >
                        <Check className="w-4 h-4 mr-2" />
                        Make kòm voye
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
};

export default WhatsAppNotifications;

